import { useState, useEffect, useRef, useCallback } from 'react';
import { useDebouncedValue } from '@mantine/hooks';

interface UseEditableFieldProps {
  value: any;
  type?: 'text' | 'number' | 'date' | 'select';
  required?: boolean;
  isUpdating?: boolean;
  onSave?: (value: any) => Promise<void>;
  min?: number;
  max?: number;
}

interface UseEditableFieldReturn {
  // UI State
  isEditing: boolean;
  isHovered: boolean;
  isSaving: boolean;
  error: string;
  
  // Value State
  editValue: any;
  displayValue: any;
  
  // Handlers
  handleEdit: () => void;
  handleCancel: () => void;
  handleSave: () => Promise<void>;
  handleChange: (value: any) => void;
  handleSelectChange: (value: string | null) => Promise<void>;
  handleDateChange: (value: Date | null) => Promise<void>;
  setIsHovered: (hovered: boolean) => void;
  
  // Refs
  inputRef: React.RefObject<HTMLInputElement>;
}

export const useEditableField = ({
  value,
  type = 'text',
  required = false,
  isUpdating = false,
  onSave,
  min,
  max,
}: UseEditableFieldProps): UseEditableFieldReturn => {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [editValue, setEditValue] = useState<any>(value);
  const [displayValue, setDisplayValue] = useState<any>(value);
  const [error, setError] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [debouncedValue] = useDebouncedValue(editValue, 1000);
  const initialValueRef = useRef<any>(value);

  const getBoundsError = useCallback(
    (val: any): string => {
      if (type !== 'number' || val === '' || val === null || val === undefined) return '';
      const num = typeof val === 'string' ? parseFloat(val) : val;
      if (typeof num !== 'number' || isNaN(num)) return '';
      if (min !== undefined && num < min) return `Value must be at least ${min}`;
      if (max !== undefined && num > max) return `Value must be at most ${max}`;
      return '';
    },
    [type, min, max]
  );

  // Transform and sync value based on type when prop changes
  useEffect(() => {
    let transformedValue = value;
    
    if (type === 'number') {
      if (value === 'N/A' || value === '' || value === null || value === undefined) {
        transformedValue = undefined;
      } else if (typeof value === 'string') {
        const parsed = parseFloat(value);
        transformedValue = isNaN(parsed) ? undefined : parsed;
      }
    }
    
    setEditValue(transformedValue);
    setDisplayValue(transformedValue);
    initialValueRef.current = transformedValue;
  }, [value, type]);

  // Auto-focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleEdit = () => {
    if (!onSave) return;
    
    // Pre-fill with current value, transforming if needed
    let valueToEdit = initialValueRef.current;
    
    if (type === 'number') {
      if (value === 'N/A' || value === '' || value === null || value === undefined) {
        valueToEdit = undefined;
      } else if (typeof value === 'string') {
        const parsed = parseFloat(value);
        valueToEdit = isNaN(parsed) ? undefined : parsed;
      } else {
        valueToEdit = value;
      }
    }
    
    setEditValue(valueToEdit);
    setIsEditing(true);
    setError('');
  };

  const handleCancel = () => {
    setEditValue(initialValueRef.current);
    setIsEditing(false);
    setError('');
  };

  const handleChange = useCallback((newValue: any) => {
    setEditValue(newValue);
  }, []);

  // Auto-save for text and number inputs with debouncing
  useEffect(() => {
    if (!isEditing || !onSave) return;
    if (type !== 'text' && type !== 'number') return;
    
    // Value reverted back to what's already saved — clear any stale error
    // (e.g. an out-of-bounds message left over from a value since undone).
    if (debouncedValue === initialValueRef.current) {
      setError('');
      return;
    }

    // Only save if value actually changed from the initial value
    if (debouncedValue !== initialValueRef.current) {
      // Don't save if value is empty/null/undefined and not required
      if (!required && (debouncedValue === '' || debouncedValue === null || debouncedValue === undefined)) {
        return;
      }
      
      // Validation for required fields
      if (required && (!debouncedValue || debouncedValue === '')) {
        setError('This field is required');
        return;
      }

      // Validation for numeric bounds (e.g. an impact score of 1-10)
      const boundsError = getBoundsError(debouncedValue);
      if (boundsError) {
        setError(boundsError);
        return;
      }

      setError('');

      // Save the debounced value
      onSave(debouncedValue).then(() => {
        initialValueRef.current = debouncedValue;
        setDisplayValue(debouncedValue);
      }).catch((err: any) => {
        setError(err.message || 'Failed to save');
      });
    }
  }, [debouncedValue, isEditing, onSave, type, required, getBoundsError]);

  const handleSave = useCallback(async () => {
    if (!onSave) return;

    // Validation
    if (required && (!editValue || editValue === '')) {
      setError('This field is required');
      return;
    }

    // Validation for numeric bounds (e.g. an impact score of 1-10)
    const boundsError = getBoundsError(editValue);
    if (boundsError) {
      setError(boundsError);
      return;
    }

    // Don't save if value hasn't changed
    if (editValue === initialValueRef.current) {
      return;
    }

    setError('');

    try {
      await onSave(editValue);
      initialValueRef.current = editValue;
      setDisplayValue(editValue);
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    }
  }, [onSave, editValue, required, getBoundsError]);

  const handleSelectChange = useCallback(async (val: string | null) => {
    const newValue = val || '';
    setEditValue(newValue);
    
    if (onSave && newValue !== initialValueRef.current) {
      try {
        await onSave(newValue);
        initialValueRef.current = newValue;
        setDisplayValue(newValue);
        setTimeout(() => setIsEditing(false), 100);
      } catch (err: any) {
        setError(err.message || 'Failed to save');
      }
    }
  }, [onSave]);

  const handleDateChange = useCallback(async (val: Date | null) => {
    const isoDate = val ? val.toISOString() : '';
    setEditValue(isoDate);
    
    if (onSave && isoDate !== initialValueRef.current) {
      try {
        await onSave(isoDate);
        initialValueRef.current = isoDate;
        setDisplayValue(isoDate);
        setTimeout(() => setIsEditing(false), 100);
      } catch (err: any) {
        setError(err.message || 'Failed to save');
      }
    }
  }, [onSave]);

  return {
    isEditing,
    isHovered,
    isSaving: isUpdating,
    error,
    editValue,
    displayValue,
    handleEdit,
    handleCancel,
    handleSave,
    handleChange,
    handleSelectChange,
    handleDateChange,
    setIsHovered,
    inputRef,
  };
};

